import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppError, ErrorType, NhsErrorCode, BackendErrorCode } from '../models/error.model';
import { HttpService } from '../utils/http.service';
import { PathsService } from './state/paths.service';

/**
 * Error Handling Service
 * 
 * Centralized service for managing application errors.
 * Provides clean error handling without exposing details in URLs.
 * 
 * Usage:
 * 1. Set error: errorService.setError({ code: 'nhs_account_account_exists', ... })
 * 2. Get error: errorService.getError().subscribe(error => { ... })
 * 3. Clear error: errorService.clearError()
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {
  private errorSubject = new BehaviorSubject<AppError | null>(null);
  public error$: Observable<AppError | null> = this.errorSubject.asObservable();

  // Error definitions library
  private readonly errorDefinitions: Record<string, Omit<AppError, 'code' | 'timestamp'>> = {
    // NHS Errors
    [NhsErrorCode.ACCOUNT_EXISTS]: {
      type: 'info',
      title: 'NHS Account Already Exists',
      message: 'Account already created. Your email has already been used to create an account. Please select <b>\'Continue with NHS\'</b> to log in.',
      targetPage: '/login',
      dismissible: true
    },
    [NhsErrorCode.NAME_MISMATCH]: {
      type: 'error',
      title: 'Name Verification Failed',
      message: 'The name on your NHS account does not match our records. This may be a different account. Please contact getUBetter support for assistance.',
      targetPage: '/guest',
      dismissible: true
    },
    [NhsErrorCode.CONSENT_NOT_GIVEN]: {
      type: 'error',
      title: 'We could not create your account',
      message: 'To create an account with us using NHS login, you’ll need to share your basic information. Select Continue to NHS login to do this. If you don’t want to share your basic information, select Create with email instead.',
      targetPage: '/create-account',
      dismissible: true
    },
    [NhsErrorCode.AGE_RESTRICTION]: {
      type: 'error',
      title: 'Age Restriction',
      message: 'You must be 18 years or older to use this service.',
      targetPage: '/guest',
      dismissible: true
    },
    [NhsErrorCode.RECURRING_USER]: {
      type: 'info',
      title: 'Recurring NHS User',
      message: 'You are already registered with an NHS account. Please log in using your NHS account.',
      targetPage: '/login',
      dismissible: true
    },
    [NhsErrorCode.LOGIN_REQUIRED]: {
      type: 'warning',
      title: 'NHS Login Required',
      message: 'You need to log in with your NHS account to access this feature.',
      targetPage: '/login',
      dismissible: true
    },
    [NhsErrorCode.CANNOT_LOGIN]: {
      type: 'error',
      title: 'Login Error',
      message: 'There was an error logging in with your NHS account. Please try again later.',
      targetPage: '/login',
      dismissible: true
    },
    [NhsErrorCode.NHS_ACCOUNT_LINKED]: {
      type: 'info',
      title: 'Your email is linked to NHS login',
      message: "Select 'Continue to NHS login' above to sign in. If you want to login with getUBetter instead, you'll need to <a style=\"color: #007cba; text-decoration: none;\" onclick=\"angularComponentRef.createNewPassword()\">create a new password</a> for your account.",
      targetPage: '/login',
      dismissible: true
    },
    
    // Backend Errors
    [BackendErrorCode.EMAIL_NOT_FOUND]: {
      type: 'error',
      title: 'Email Not Found',
      message: 'No account found with this email address.',
      targetPage: '/login',
      dismissible: false
    },
    [BackendErrorCode.INCORRECT_PASSWORD]: {
      type: 'error',
      title: 'Incorrect Password',
      message: 'The password you entered is incorrect.',
      targetPage: '/login',
      dismissible: false
    },
    [BackendErrorCode.MFA_CODE_ERROR]: {
      type: 'error',
      title: 'Error generating MFA code',
      message: 'Please try again later',
      targetPage: '/login',
      dismissible: true
    },
    [BackendErrorCode.FORGOT_SUCCESS]: {
      type: 'success',
      title: 'Password Reset Email Sent',
      message: 'Please check your email for instructions to reset your password.',
      targetPage: '/login',
      dismissible: true
    }
  };

  constructor(
    private httpService: HttpService,
    private pathsService: PathsService
  ) {}

  /**
   * Set an error using predefined error code
   * Automatically resolves target page for NHS errors based on user flow
   */
  async setError(errorCode: string, params?: Record<string, any>): Promise<void> {
    const definition = this.errorDefinitions[errorCode];

    if (definition) {
      // Get last visited URLs for NHS errors to determine correct target page
      let targetPage = definition.targetPage;
      
      // Check if this is an NHS flow error that needs dynamic routing
      const nhsFlowErrors = [
        NhsErrorCode.CONSENT_NOT_GIVEN,
        NhsErrorCode.CANNOT_LOGIN,
        NhsErrorCode.AGE_RESTRICTION
      ];

      if (nhsFlowErrors.includes(errorCode as NhsErrorCode)) {
        try {
          const lastUrl = localStorage.getItem('lastVisitedUrl') || undefined;

          targetPage = this.getNhsErrorTargetPage(errorCode, lastUrl);
          console.log('[ErrorService] Resolved NHS error target page:', targetPage, 'for error:', errorCode);
        } catch (error) {
          console.warn('[ErrorService] Could not get last visited URLs, using default target page:', error);
        }
      }

      const appError: AppError = {
        code: errorCode,
        type: definition.type,
        title: definition.title,
        message: definition.message,
        targetPage: targetPage,
        dismissible: definition.dismissible,
        params,
        timestamp: Date.now()
      };
      this.errorSubject.next(appError);
    } else {
      // Fallback for undefined errors
      console.warn(`Error code '${errorCode}' not found in definitions`);
      this.httpService.getError(errorCode).then(apiError => {

        try {
          if (apiError) {
            // Ensure we set basic fields required by AppError
            const error: AppError = {
              code: errorCode,
              type: apiError.type || 'error',
              title: apiError.title || 'An error occurred',
              message: apiError.message || '',
              targetPage: apiError.targetPage || '/guest',
              dismissible: apiError.dismissible !== undefined ? apiError.dismissible : true,
              params: apiError.params || params,
              timestamp: Date.now()
            };
            this.errorSubject.next(error);
            return;
          }
        } catch (err) {
          console.warn('Failed to fetch error from API:', err);
        }


        this.setCustomError({
          code: errorCode,
          type: 'error',
          title: 'An error occurred',
          message: 'Please try again later.',
          targetPage: '/guest',
          params
        });

      });
    }
  }

  /**
   * Set a custom error with full control
   */
  setCustomError(error: Omit<AppError, 'timestamp'>): void {
    this.errorSubject.next({
      ...error,
      timestamp: Date.now()
    });
  }

  /**
   * Get current error as observable
   */
  getError(): Observable<AppError | null> {
    return this.error$;
  }

  /**
   * Get current error value (synchronous)
   */
  getCurrentError(): AppError | null {
    return this.errorSubject.value;
  }

  /**
   * Check if there is an active error
   */
  hasError(): boolean {
    return this.errorSubject.value !== null;
  }

  /**
   * Check if error is for specific page
   */
  hasErrorForPage(pagePath: string): boolean {
    const error = this.errorSubject.value;
    return error !== null && error.targetPage === pagePath;
  }

  /**
   * Clear current error
   */
  clearError(): void {
    this.errorSubject.next(null);
  }

  /**
   * Get error definition by code (for preview/testing)
   */
  getErrorDefinition(errorCode: string): Omit<AppError, 'code' | 'timestamp'> | undefined {
    return this.errorDefinitions[errorCode];
  }

  /**
   * Get all available error codes
   */
  getAvailableErrorCodes(): string[] {
    return Object.keys(this.errorDefinitions);
  }

  /**
   * Get appropriate target page for NHS errors based on user flow
   * Uses lastVisitedUrl to determine if user was on login or registration
   * @param errorCode - The NHS error code
   * @param lastVisitedUrl - The last page the user was on (from PathsService)
   * @param secondLastVisitedUrl - The second-to-last page the user was on
   * @returns The appropriate target page path
   */
  getNhsErrorTargetPage(errorCode: string, lastVisitedUrl?: string): string {
    // Check if this is an NHS error that can appear on both login and registration
    const nhsFlowErrors = [
      NhsErrorCode.CONSENT_NOT_GIVEN,
      NhsErrorCode.CANNOT_LOGIN,
      NhsErrorCode.AGE_RESTRICTION
    ];
    const errorDef = this.errorDefinitions[errorCode];
    console.log('[ErrorService] Target page resolution for NHS error:', errorDef.targetPage);

    // If it's a flow error, determine target based on lastVisitedUrl
    if (nhsFlowErrors.includes(errorCode as NhsErrorCode)) {
      console.log('[ErrorService] Checking NHS error target page for:', errorCode, 'lastVisitedUrl:', lastVisitedUrl);
      
      return errorDef?.targetPage || '/login';
    } 
    else {
      // Non-flow NHS errors use their predefined target page
      console.log('[ErrorService] Non-flow NHS error, using default target page:', errorDef?.targetPage || '/guest');
      return errorDef?.targetPage || '/guest';
    }
  }
}
